import { Component, HostListener, OnInit, signal } from '@angular/core';
import { faAngleLeft, faPlus, faSortUp, faSortDown, faFilter, faCircle, faEye, faEyeSlash, faRotateLeft, faFileDownload, faSortAmountDownAlt } from '@fortawesome/free-solid-svg-icons';
import { Location, NgIf, NgTemplateOutlet, NgFor } from '@angular/common';
import { NgbModal, NgbTooltip, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbPopover, NgbPagination, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { TableControlService } from '../table-control.service';
import { environment } from 'src/environments/environment';
import { LoaderService } from '../shared/services/loader.service';
import { Subject, debounceTime } from 'rxjs';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { NgxSpinnerComponent } from 'ngx-spinner';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MultiYearAgreementService } from '@app-shared/services/multi-year-agreement.service';
import { MultiYearAgreementStatusPipe } from '@app-shared/pipes/multi-year-agreements.pipe';
import { MultiYearAgreement } from '@app-shared/interfaces/multi-year-agreement/multi-year-agreement';
import { NewMultiYearAgreementModalComponent } from '@app-shared/modals/new-multi-year-agreement-modal/new-multi-year-agreement-modal.component';
import { MultiYearAgreementResponse } from '../shared/interfaces/multi-year-agreement/multi-year-agreement';
@Component({
  selector: 'app-multi-year-agreements',
  templateUrl: './multi-year-agreements.component.html',
  styleUrl: './multi-year-agreements.component.css',
  providers: [MultiYearAgreementStatusPipe],
  imports: [NgbTooltip, FaIconComponent, NgIf, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbPopover, NgFor, RouterLink, NgbPagination, NgbCollapse, NgTemplateOutlet, NgxSpinnerComponent, FormatDateTimePipe, MultiYearAgreementStatusPipe]
})
export class MultiYearAgreementsComponent implements OnInit {
  //Screensize event Listener
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.resizeSubject.next((event.target as Window).innerWidth);
  }

  // Variable Subject
  private resizeSubject: Subject<number> = new Subject<number>();

  // Properties
  multiYearAgreements = signal<MultiYearAgreement[]>([]);
  multiYearAgreementsCount = signal<number>(0);
  isMobileView = signal<boolean>(false);

  sortOptions = [
    { label: 'Client', value: 'c.client_name' },
    { label: 'Site', value: 's.site_address_city' },
    { label: 'Start Year', value: 'm.mya_start_year' },
    { label: 'Agreement Length', value: 'm.mya_length' },
    { label: 'Status', value: 'm.mya_status' },
    { label: 'Updated', value: 'm.mya_updated_at' },
  ];
  filterColumns = [
    { label: 'Client', value: 'c.client_name' },
    { label: 'Start Year', value: 'm.mya_start_year' },
    { label: 'Agreement Length', value: 'm.mya_length' },
    { label: 'Status', value: 'm.mya_status' },
  ];
  sortBy: string = 'm.mya_start_year';
  sortDirection: string = 'ASC';

  // Actions
  selectedClientId: string;

  // Table Controls
  tableControls: TableControlService;
  storedSearchValue: string;

  // Filters
  columnFilterValues = new Map();
  activeFilters = new Map();
  activeFilterCount: number = 0;
  selectedFilterColumn: string = '';
  hasActiveFiltersValue: boolean = false;

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faPlus = faPlus;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faFilter = faFilter;
  faCircle = faCircle;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faRotateLeft = faRotateLeft;
  faFileDownload = faFileDownload;
  faSortAmountDownAlt = faSortAmountDownAlt;

  // Get isMobile value in reactive way
  public get isMobile(): boolean {
    return this.isMobileView();
  }

  constructor(
    private multiYearAgreementStatusPipe: MultiYearAgreementStatusPipe,
    public location: Location,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private multiYearAgreementService: MultiYearAgreementService) {
    this.resizeSubject.pipe(
      debounceTime(100)
    ).subscribe(width => {
      if (width <= 768) {
        this.checkScreenSize(width);
      } else {
        this.isMobileView.set(false);
        this.tableControls.setLimit(20);
      }
    });
  }

  ngOnInit(): void {
    this.tableControls = new TableControlService('MultiYearAgreements', true, 'm.mya_start_year', 'DESC');
    this.checkScreenSize();
    if (this.isMobile || this.isMobileView()) {
      let activeFilters = this.tableControls.getActiveFilters()
      let columns = [];
      for (const [key] of activeFilters) {
        columns.push(key)
      }
      this.tableControls.resetFilters(columns)
    }
    this.tableControls.refresh.subscribe(() => {
      this.getMultiYearAgreements();
      this.updateHasActiveFilters();
    });
    if (this.tableControls.hasTableConfiguration()) this.storedSearchValue = this.tableControls.getSearchTerm();
    this.getMultiYearAgreements();
    setTimeout(() => this.updateHasActiveFilters(), 0);
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
    this.resizeSubject.unsubscribe();
  }

  // Open New Project Modal
  openNewMyaModal(): void {
    this.modalService.open(NewMultiYearAgreementModalComponent);
  }

  // Get MultiYear Agreements
  private getMultiYearAgreements(): void {
    this.loaderService.showSpinner();
    let params = this.tableControls.getParams();
    if (this.isMobile) {
      params.sortBy = this.sortBy;
      params.sortDirection = this.sortDirection;

      for (const [key, value] of this.activeFilters) {
        if (key.includes('range')) {
          params[`filter:${key}`] = value;
        } else {
          params[`filter:${key}`] = JSON.stringify(value);
        }
      }
    }
    this.multiYearAgreementService.getMultiYearAgreements(params).subscribe({
      next: (res: MultiYearAgreementResponse) => {
        this.multiYearAgreements.set(res?.multiYearAgreements ?? []);
        this.multiYearAgreementsCount.set(res?.count ?? 0);
        this.updateHasActiveFilters();
        this.loaderService.hideSpinner();
      },
      error: () => {
        this.loaderService.hideSpinner();
      }
    });
  }

  updateHasActiveFilters(): void {
    if (this.multiYearAgreementsCount() === 0) {
      this.hasActiveFiltersValue = false;
      return;
    }
    if (this.isMobile) {
      this.hasActiveFiltersValue = this.activeFilters.size > 0 || !!(this.tableControls.getSearchTerm() && this.tableControls.getSearchTerm().length > 0);
    } else {
      const tableFilters = this.tableControls.getActiveFilters();
      this.hasActiveFiltersValue = tableFilters.size > 0 || !!(this.tableControls.getSearchTerm() && this.tableControls.getSearchTerm().length > 0);
    }
  }

  // Download Multi-Year Agreements
  downloadMultiYearAgreements(): void {
    window.open(`${environment.apiUrl}/multiYearAgreements/export`);
  }

  /* ----- Filters and Sorting ----- */

  // Get Distinct Column Values
  getDistinctColumnValues(column: string): void {
    this.selectedFilterColumn = column;
    if (!this.columnFilterValues.has(column)) {
      const params = {
        searchTerm: null,
        sortBy: column,
        sortDirection: 'ASC',
        limit: null,
        offset: null,
        column: column
      };
      if (this.isMobile) {
        for (const [key, value] of this.activeFilters) {
          if (key.includes('range')) params[`filter:${key}`] = value;
          else params[`filter:${key}`] = JSON.stringify(value);
        }
      }
      this.multiYearAgreementService.getDistinctColumnValues(params).subscribe((values) => {
        const tempArray = [];
        for (let value of values) {
          let name = null;
          switch (column) {
            case 'm.mya_status':
              name = this.multiYearAgreementStatusPipe.transform(value);
              break;
            default:
              name = (value === null || value.length == 0) ? 'BLANK' : value;
              break;
          }
          tempArray.push({ name: name, value: value, randomId: Math.random().toString(36) });
        }
        this.columnFilterValues.set(column, tempArray);
      });
    }
  }

  // Sorting
  sorting(column: string): void {
    if (column != this.sortBy) this.sortDirection = 'ASC';
    if (column == this.sortBy) this.sortDirection = (this.sortDirection == 'ASC') ? 'DESC' : 'ASC';
    this.sortBy = column;
    this.getMultiYearAgreements();
  }

  // Toggle Filter
  toggleFilter(column: string, value: string): void {
    if (this.activeFilters.has(column)) {
      if (this.activeFilters.get(column).includes(value)) {
        let tempArray = this.activeFilters.get(column);
        tempArray = tempArray.filter((filterValue) => { return filterValue != value; });
        if (tempArray.length == 0) {
          this.activeFilters.delete(column);
        } else {
          this.activeFilters.set(column, tempArray);
        }
      } else {
        const tempArray = this.activeFilters.get(column);
        tempArray.push(value);
        this.activeFilters.set(column, tempArray);
      }
    } else {
      this.activeFilters.set(column, [value]);
    }
    this.getMultiYearAgreements();
    this.getActiveFilterCount();
    this.updateHasActiveFilters();
  }

  // Get Active Filter Count
  getActiveFilterCount(): void {
    this.activeFilterCount = 0;
    for (const value of this.activeFilters.values()) {
      this.activeFilterCount += value.length;
    }
  }

  // Filter Exists
  filterExists(column: string, value: string): boolean {
    return this.activeFilters.has(column) ? this.activeFilters.get(column).includes(value) : false;
  }

  // Reset Active Filters
  resetActiveFilters(): void {
    this.activeFilters.clear();
    this.getMultiYearAgreements();
    this.getActiveFilterCount();
    this.updateHasActiveFilters();
  }

  // Show Column Filter Popover
  showColumnFilterPopover(popover: NgbPopover, column: string): void {
    this.getDistinctColumnValues(column);
    if (popover.isOpen()) {
      popover.close();
    } else {
      setTimeout(() => {
        popover.open({ column });
      }, 250);
    }
  }

  //Set isMobile value and set table limit base on screen size
  checkScreenSize(width?: number) {
    let screenWidth = width || window.innerWidth;
    this.isMobileView.set(screenWidth <= 768);
    this.isMobileView() ? this.tableControls.setLimit(5) : this.tableControls.setLimit(20);
  }
}
